/**
 * TripSection — Darstellung einer Fahrt (Vormittag oder Nachmittag) in der
 * Tageskarte.
 *
 * Kopf mit Richtung + Meta (Zeit · Transport · Treffpunkt) und „Details"-Button
 * (öffnet den Detail-Drawer für die angezeigte Person). Darunter die inline
 * Statuseingabe und die Namens-Chips. Im Admin-Modus sind die Chips antippbar
 * (öffnen den Drawer direkt für diese Person); Mitglieder ohne Eintrag
 * erscheinen als gestrichelte „Name · Offen"-Chips.
 */

import { useState } from "react";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import Collapse from "@mui/material/Collapse";
import Link from "@mui/material/Link";
import ExpandMore from "@mui/icons-material/ExpandMore";
import { useTheme } from "@mui/material/styles";
import { Link as RouterLink } from "react-router-dom";
import { useI18n } from "../../i18n/I18nProvider";
import StatusToggle from "./StatusToggle";
import PersonChip from "./PersonChip";
import { statusOf } from "./helpers";
import type {
  AvailabilityStatus,
  SimpleChild,
  TripAction,
  TripView,
} from "./types";

export default function TripSection({
  trip,
  wide,
  displayedUserId,
  displayedName,
  isSelf,
  adminView,
  personChildren,
  disabled,
  onAction,
  onOpenDetails,
  onOpenPersonDetails,
}: {
  trip: TripView;
  /** Ab `md` werden Hin-/Rückweg nebeneinander gelegt (Rahmen links statt oben). */
  wide: boolean;
  displayedUserId: string | undefined;
  displayedName: string;
  isSelf: boolean;
  adminView: boolean;
  /** Kinder der angezeigten Person (eigene oder, im Admin-Modus, fremde). */
  personChildren: SimpleChild[];
  disabled: boolean;
  onAction: (action: TripAction) => void;
  onOpenDetails: () => void;
  onOpenPersonDetails: (userId: string, name: string) => void;
}) {
  const { t } = useI18n();
  const theme = useTheme();

  // Passendes Banner je nach Hell-/Dunkelmodus (Dateien in public/).
  const bannerSrc =
    theme.palette.mode === "dark"
      ? "/se-group-appbar-dunkel.png"
      : "/se-group-appbar-hell.png";

  // „Verhindert" und „Vertreter" sind je einzeln per Button ein-/ausklappbar.
  const [showBlocked, setShowBlocked] = useState(false);
  const [showSubstitutes, setShowSubstitutes] = useState(false);

  const directionLabel =
    trip.direction === "afternoon"
      ? t("weeklyPlan.direction.afternoon")
      : t("weeklyPlan.direction.morning");

  const meta = [
    trip.startTime,
    t(`weeklyPlan.transport.${trip.transportMode}`),
    trip.meetingPoint,
  ]
    .filter(Boolean)
    .join(" · ");

  const myStatus = statusOf(trip, displayedUserId);

  function setStatus(next: AvailabilityStatus | null) {
    if (displayedUserId == null) return;
    if (next === null) {
      onAction(
        isSelf
          ? { kind: "clearOwn", tripId: trip.id }
          : { kind: "clearMember", tripId: trip.id, userId: displayedUserId, name: displayedName },
      );
    } else {
      onAction(
        isSelf
          ? { kind: "setOwn", tripId: trip.id, status: next }
          : { kind: "setMember", tripId: trip.id, userId: displayedUserId, name: displayedName, status: next },
      );
    }
  }

  // Chips aller Mitglieder mit Eintrag (nach Status gruppiert) ...
  const entered: { userId: string; name: string; status: AvailabilityStatus; setByAdmin: boolean }[] =
    (["wish", "optional", "blocker"] as AvailabilityStatus[]).flatMap((s) =>
      trip[s].map((m) => ({
        userId: m.userId,
        name: m.name,
        status: s,
        setByAdmin: m.setByAdmin,
      })),
    );
  const childChips = trip.children;

  // Nach Status getrennte Gruppen.
  const blockedMembers = entered.filter((m) => m.status === "blocker");
  const substituteMembers = entered.filter((m) => m.status === "optional");
  const ridingMembers = entered.filter((m) => m.status === "wish");

  const hasBlocked = blockedMembers.length > 0;
  const hasSubstitutes = substituteMembers.length > 0;
  const hasRiders = ridingMembers.length > 0 || childChips.length > 0;
  const hasPassengers = hasBlocked || hasSubstitutes || hasRiders;

  // Kinder der angezeigten Person, die bereits an dieser Fahrt teilnehmen.
  const participatingChildIds = new Set(trip.children.map((c) => c.childId));

  return (
    <Box
      sx={{
        px: 2,
        py: 1.75,
        flex: 1,
        minWidth: 0,
        borderTop: trip.direction === "afternoon" && !wide ? 1 : 0,
        borderLeft: trip.direction === "afternoon" && wide ? 1 : 0,
        borderColor: "divider",
      }}
    >
      <Stack direction="row" alignItems="center" mb={1.25}>
        <Box flex={1}>
          <Typography fontWeight={700}>{directionLabel}</Typography>
          <Typography variant="body2" color="text.secondary" minHeight={20}>
            {meta}
          </Typography>
        </Box>
        <Button
          variant="text"
          size="medium"
          onClick={onOpenDetails}
          aria-label={t("weeklyPlan.trip.detailsAria", { direction: directionLabel })}
          sx={{ minHeight: 44, bgcolor: "action.hover" }}
        >
          {t("weeklyPlan.trip.details")}
        </Button>
      </Stack>

      <StatusToggle
        value={myStatus}
        disabled={disabled}
        ariaLabel={
          isSelf
            ? t("weeklyPlan.status.ownAria", { direction: directionLabel })
            : t("weeklyPlan.status.memberAria", { name: displayedName })
        }
        onChange={setStatus}
      />

      {/* Eigene Kinder direkt unter der Statuseingabe wählen (Req 3). */}
      {personChildren.length > 0 ? (
        <Stack
          direction="row"
          gap={1}
          flexWrap="wrap"
          mt={1.25}
          role="group"
          aria-label={t("weeklyPlan.children.ownAria", { direction: directionLabel })}
        >
          {personChildren.map((c) => {
            const participating = participatingChildIds.has(c.id);
            return (
              <Chip
                key={c.id}
                label={c.name}
                color="secondary"
                variant={participating ? "filled" : "outlined"}
                disabled={disabled}
                aria-pressed={participating}
                onClick={() =>
                  onAction(
                    participating
                      ? { kind: "clearChild", tripId: trip.id, childId: c.id }
                      : { kind: "setChild", tripId: trip.id, childId: c.id },
                  )
                }
                sx={{ height: 44, fontSize: 15, px: 1 }}
              />
            );
          })}
        </Stack>
      ) : (
        isSelf && (
          // Noch keine Kinder: Hinweis + Link zum Profil (Req 4).
          <Typography variant="body2" color="text.secondary" mt={1.25}>
            {t("weeklyPlan.children.noneHint")}{" "}
            <Link component={RouterLink} to="/profile">
              {t("weeklyPlan.children.createLink")}
            </Link>
          </Typography>
        )
      )}

      {/* Mitfahrer + aufklappbare „Verhindert"/„Vertreter"-Spalten. */}
      {hasPassengers && (
        <Box mt={1.5}>
          {(hasBlocked || hasSubstitutes) && (
            <Box
              sx={{
                display: "flex",
                flexWrap: "wrap",
                gap: 1.5,
                "& > *": { flex: "1 1 140px", minWidth: 0 },
              }}
            >
              {/* „Verhindert": Button ist zugleich die Überschrift, blendet die Chips ein/aus. */}
              {hasBlocked && (
                <Box>
                  <Button
                    variant="text"
                    size="small"
                    fullWidth
                    onClick={() => setShowBlocked((v) => !v)}
                    aria-expanded={showBlocked}
                    startIcon={
                      <ExpandMore
                        sx={{
                          transition: "transform 0.2s",
                          transform: showBlocked ? "rotate(180deg)" : "none",
                        }}
                      />
                    }
                    sx={{
                      justifyContent: "flex-start",
                      color: "text.secondary",
                      fontSize: 12,
                      fontWeight: 400,
                      px: 0.5,
                      minHeight: 32,
                    }}
                  >
                    {t("weeklyPlan.trip.blocked")}
                  </Button>
                  <Collapse in={showBlocked} unmountOnExit>
                    <Stack direction="row" flexWrap="wrap" gap={0.75} mt={0.5}>
                      {blockedMembers.map((m) => (
                        <PersonChip
                          key={m.userId}
                          name={m.name}
                          status={m.status}
                          highlighted={m.userId === displayedUserId}
                          setByAdmin={m.setByAdmin}
                          onClick={adminView ? () => onOpenPersonDetails(m.userId, m.name) : undefined}
                        />
                      ))}
                    </Stack>
                  </Collapse>
                </Box>
              )}

              {/* „Vertreter": Button ist zugleich die Überschrift, blendet die Chips ein/aus. */}
              {hasSubstitutes && (
                <Box>
                  <Button
                    variant="text"
                    size="small"
                    fullWidth
                    onClick={() => setShowSubstitutes((v) => !v)}
                    aria-expanded={showSubstitutes}
                    startIcon={
                      <ExpandMore
                        sx={{
                          transition: "transform 0.2s",
                          transform: showSubstitutes ? "rotate(180deg)" : "none",
                        }}
                      />
                    }
                    sx={{
                      justifyContent: "flex-start",
                      color: "text.secondary",
                      fontSize: 12,
                      fontWeight: 400,
                      px: 0.5,
                      minHeight: 32,
                    }}
                  >
                    {t("weeklyPlan.trip.substitutes")}
                  </Button>
                  <Collapse in={showSubstitutes} unmountOnExit>
                    <Stack direction="row" flexWrap="wrap" gap={0.75} mt={0.5}>
                      {substituteMembers.map((m) => (
                        <PersonChip
                          key={m.userId}
                          name={m.name}
                          status={m.status}
                          highlighted={m.userId === displayedUserId}
                          setByAdmin={m.setByAdmin}
                          onClick={adminView ? () => onOpenPersonDetails(m.userId, m.name) : undefined}
                        />
                      ))}
                    </Stack>
                  </Collapse>
                </Box>
              )}
            </Box>
          )}

          {hasRiders && (
            <>
              {/* Statt „Mitfahrer"-Divider: optionaler „Unterwegs"-Text + Banner-Bild
                  (helle/dunkle Variante je nach Theme-Modus). */}
              <Stack
                direction="row"
                alignItems="center"
                gap={1}
                sx={{ mt: hasBlocked || hasSubstitutes ? 1 : 0 }}
              >
                {t("weeklyPlan.trip.onTheWay") && (
                  <Typography fontWeight={700}>
                    {t("weeklyPlan.trip.onTheWay")}
                  </Typography>
                )}
                <Box
                  component="img"
                  src={bannerSrc}
                  alt={t("weeklyPlan.trip.passengers")}
                  sx={{
                    height: 28,
                    width: "auto",
                    display: "block",
                    // Nachmittag = Rückweg: Räder in die andere Richtung spiegeln.
                    transform: trip.direction === "afternoon" ? "scaleX(-1)" : "none",
                  }}
                />
              </Stack>
              <Stack direction="row" flexWrap="wrap" gap={0.75} mt={0.75}>
                {ridingMembers.map((m) => (
                  <PersonChip
                    key={m.userId}
                    name={m.name}
                    status={m.status}
                    highlighted={m.userId === displayedUserId}
                    setByAdmin={m.setByAdmin}
                    onClick={adminView ? () => onOpenPersonDetails(m.userId, m.name) : undefined}
                  />
                ))}
                {childChips.map((c) => (
                  <Chip
                    key={`child-${c.childId}`}
                    size="small"
                    label={c.name}
                    color="secondary"
                    variant="outlined"
                  />
                ))}
              </Stack>
            </>
          )}
        </Box>
      )}
    </Box>
  );
}
